import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Users.css';

interface User {
  _id: string;
  email: string;
  name: string;
  level: number;
  companyName?: string;
  firstName?: string;
  lastName?: string;
  billingEmail?: string;
  phoneNumber?: string;
  isCurrentUser?: boolean;
}

const Users: React.FC = () => {
  const { token, currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [promotingUserId, setPromotingUserId] = useState<string | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  
  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('');

  // Check if any filter is active
  const isFilterActive = searchQuery !== '' || levelFilter !== '';

  // Fetch all users
  useEffect(() => {
    const fetchUsers = async () => {
      if (!token) return;
      
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/all`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Mark the current user
        const processedUsers = data.map((user: User) => ({
          ...user,
          isCurrentUser: user._id === currentUser?._id
        }));
        
        setUsers(processedUsers);
        setFilteredUsers(processedUsers);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch users:', err);
        setError('Failed to load users. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, [token, currentUser]);

  // Filter users based on search query and level
  useEffect(() => {
    if (!users.length) return;

    let result = [...users];
    
    // Apply search filter (name or email)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(user => 
        user.name.toLowerCase().includes(query) || 
        user.email.toLowerCase().includes(query)
      );
    }
    
    // Apply level filter
    if (levelFilter) {
      // Convert displayed level (1,2,3) to database level (0,1,2)
      const dbLevel = parseInt(levelFilter) - 1;
      result = result.filter(user => user.level === dbLevel);
    }
    
    // Sort to put current user at the top
    result.sort((a, b) => {
      if (a.isCurrentUser) return -1;
      if (b.isCurrentUser) return 1;
      return 0;
    });
    
    setFilteredUsers(result);
  }, [users, searchQuery, levelFilter]);

  // Clear all search fields
  const clearSearchFields = () => {
    setSearchQuery('');
    setLevelFilter('');
  };

  // Handle user promotion or demotion
  const handlePromote = async (userId: string, currentLevel: number) => {
    setPromotingUserId(userId);
    
    try {
      // If at max level (level 2 in DB = level 3 displayed), we'll demote to level 0 (displayed as level 1)
      const newLevel = currentLevel >= 2 ? 0 : currentLevel + 1;
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/promote/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ level: newLevel }) // Send the new level to the API
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      
      // Update the user's level locally
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user._id === userId 
            ? { ...user, level: newLevel } 
            : user
        )
      );
      
    } catch (err) {
      console.error('Failed to update user level:', err);
      alert('Failed to update user level. Please try again.');
    } finally {
      setPromotingUserId(null);
    }
  };

  // Handle user deletion
  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    console.log(userId);

    setDeletingUserId(userId);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId: userId }) // Make sure we're sending the correct userId
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      
      // Remove the deleted user from both states
      setUsers(prevUsers => prevUsers.filter(user => user._id !== userId));
      setFilteredUsers(prevUsers => prevUsers.filter(user => user._id !== userId));
      
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert('Failed to delete user. Please try again.');
    } finally {
      setDeletingUserId(null);
    }
  };

  // Get CSS class for user level
  const getLevelClass = (level: number): string => {
    // Convert DB level (0,1,2) to display level class (1,2,3)
    switch (level) {
      case 0: return 'level-one';
      case 1: return 'level-two';
      case 2: return 'level-three';
      default: return 'level-one';
    }
  };

  // Get display text for user level
  const getLevelDisplay = (level: number): string => {
    // Convert DB level (0,1,2) to display level (1,2,3)
    return (level + 1).toString();
  };

  // Get button text based on user level
  const getButtonText = (level: number, isPromoting: boolean): string => {
    if (isPromoting) return 'Updating...';
    return level >= 2 ? '⬇️ Demote' : '⬆️ Promote';
  };

  return (
    <div className="users-container">
      <div className="users-header">
        <div className="header-row">
          <h1 className="users-title">Customer List</h1>
          
          <div className="search-controls-wrapper">
            <div className="search-controls">
              <div className="search-field">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by name or email"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <span className="search-icon">🔍</span>
              </div>
              
              <div className="level-filter">
                <select
                  className="level-select"
                  value={levelFilter}
                  onChange={(e) => setLevelFilter(e.target.value)}
                >
                  <option value="">All levels</option>
                  <option value="1">Level 1</option>
                  <option value="2">Level 2</option>
                  <option value="3">Level 3</option>
                </select>
              </div>
            </div>
            
            {isFilterActive && (
              <button 
                className="clear-search-button"
                onClick={clearSearchFields}
                title="Clear all filters"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>
      
      <div className="users-list">
        {loading ? (
          <div className="loading-message">Loading users...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : filteredUsers.length === 0 ? (
          <div className="no-results">
            <p>No users found matching your search criteria.</p>
          </div>
        ) : (
          filteredUsers.map(user => (
            <div 
              key={user._id} 
              className={`user-row ${user.isCurrentUser ? 'current-user' : ''}`}
            >
              <div className="user-details">
                <div className="user-avatar">{user.name.charAt(0).toUpperCase()}</div>
                <div className="user-text">
                  <div className="user-name">
                    {user.name} 
                    {user.isCurrentUser && <span className="current-user-badge">You</span>}
                  </div>
                  <div className="user-email">{user.email}</div>
                </div>
              </div>
              
              <div className="user-actions">
                <div className={`user-level ${getLevelClass(user.level)}`}>
                  Level {getLevelDisplay(user.level)}
                </div>
                
                {!user.isCurrentUser && (
                  <>
                    <button 
                      className={`promote-button ${promotingUserId === user._id ? 'promoting' : ''}`}
                      onClick={() => handlePromote(user._id, user.level)}
                      disabled={promotingUserId === user._id}
                      title={user.level >= 2 ? 'Demote user to level 1' : 'Promote user to next level'}
                    >
                      {getButtonText(user.level, promotingUserId === user._id)}
                    </button>
                    
                    <button 
                      className={`delete-button ${deletingUserId === user._id ? 'deleting' : ''}`}
                      onClick={() => handleDeleteUser(user._id)}
                      disabled={deletingUserId === user._id}
                      title="Delete user"
                    >
                      {deletingUserId === user._id ? (
                        <span className="deleting-text">Deleting...</span>
                      ) : (
                        <span className="delete-icon">🗑️</span>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Users;